import PageHero from "@/components/PageHero";
import { Container, FadeIn, SectionHeading, SectionLabel } from "@/components/Section";
import { XR_OFFERINGS, IMG } from "@/lib/data";
import { Link } from "react-router-dom";
import { Cpu, Layers, Eye, Headphones } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";

const FEATURES = [
  {
    Icon: Cpu,
    t: "Kulture Lab",
    d: "Our R&D and production studio building immersive cultural worlds in-house.",
  },
  {
    Icon: Layers,
    t: "Projection Mapping",
    d: "We turn rooms, façades and bodies into living canvases.",
  },
  {
    Icon: Eye,
    t: "XR Theatre & AR / VR",
    d: "Walk inside live story-worlds — audiences as participants.",
  },
  {
    Icon: Headphones,
    t: "AI Storytelling",
    d: "Generative story engines and adaptive cultural narratives.",
  },
];

export default function XRStudio() {
  return (
    <div data-testid="xr-studio-page">
      <SEOHead
        cmsPage="xr-studio"
        title="XR Studio · Immersive Production · Projection Mapping & Volumetric Capture · Temple of Kulture"
        description="Our in-house XR, projection mapping and AI storytelling studio for theatres, brands, councils & festivals — from concept to volumetric capture, headset-based VR and live projection productions."
        image={IMG.xr}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Immersive XR & projection production",
          provider: { "@type": "Organization", name: "Temple of Kulture" },
          areaServed: "United Kingdom",
        }}
      />
      <PageHero
        cmsPage="xr-studio"
        eyebrow="XR & Immersive Studio"
        title={
          <>
            Worlds you can <br />
            <span className="text-amber-400">walk inside.</span>
          </>
        }
        subtitle="The future-facing wing of Temple of Kulture — XR theatre, projection mapping, AR/VR, AI storytelling and immersive digital playgrounds."
        image={IMG.xr}
        testId="xr-hero"
      />

      <section className="py-24 sm:py-32">
        <Container>
          <div className="grid lg:grid-cols-2 gap-4 mb-20">
            {FEATURES.map((f, i) => (
              <FadeIn key={f.t} delay={i * 0.06}>
                <div className="border border-white/5 hover:border-amber-400/40 bg-zinc-950 p-8 transition-colors h-full">
                  <f.Icon className="h-8 w-8 text-amber-400" />
                  <h3 className="mt-6 font-display font-bold text-2xl">{f.t}</h3>
                  <p className="mt-3 text-zinc-300 leading-relaxed">{f.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn>
            <SectionLabel>What we make</SectionLabel>
            <SectionHeading className="mt-6 mb-14">
              The full <span className="text-amber-400">XR stack.</span>
            </SectionHeading>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {XR_OFFERINGS.map((o, i) => (
              <FadeIn key={o.title} delay={i * 0.05}>
                <Link
                  to={o.href || "/corporate"}
                  data-testid={`xr-offering-${i}`}
                  className="group block relative aspect-[3/4] overflow-hidden border border-white/5 hover:border-amber-400/40 transition-colors"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                    style={{ backgroundImage: `url(${o.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <h3 className="font-display font-bold text-xl leading-tight">
                      {o.title}
                    </h3>
                    <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
                      {o.desc}
                    </p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>

          <div className="mt-20 grid lg:grid-cols-2 gap-12 items-center border-t border-white/5 pt-20">
            <FadeIn>
              <SectionLabel>Work with the Lab</SectionLabel>
              <SectionHeading className="mt-6">
                Commission a world.
              </SectionHeading>
              <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-md">
                Brands, festivals, museums and cultural institutions commission
                bespoke pieces from Kulture Lab. Talk to us about your next
                world.
              </p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <Link
                to="/corporate"
                data-testid="xr-commission-cta"
                className="inline-flex items-center bg-amber-400 hover:bg-amber-300 text-black px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
              >
                Start an enquiry
              </Link>
            </FadeIn>
          </div>
        </Container>
      </section>
    </div>
  );
}
